import React, { useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AxiosInstance } from 'axios';
import { ReduxSelectorProps } from '../../../services/constants';
import {
  IProductVariant,
  IStoreProduct,
} from '../../../interface/storeInterface';
import checkoutService from '../../../api/checkoutService';
import { FieldLabel } from '@measured/puck';
import CustomTooltip from '../../../../components/common/tooltip';
import Select from '../../../../components/common/select';
import { Loader } from '../../../../components/common/loader';
import AutoCompleteSelect from '../../../../components/common/selectAutoComplete';
import { debounce } from 'lodash';

export type CartProductsPropsCustom1 = {
  value: CartProductsProps | null;
  onChange: (_value: CartProductsProps) => void;
} & ReduxSelectorProps & { baseService: AxiosInstance };

export interface CartProductsProps {
  product: IStoreProduct | null;
  variant?: IProductVariant | null;
}

const ShipmentProducts = ({
  value,
  onChange,
  baseService,
}: CartProductsPropsCustom1) => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<IStoreProduct[]>([]);
  const storeID = searchParams.get('storeID') || '';
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<{ page: number; title: string }>({
    page: 0,
    title: '',
  });
  const position = useRef<number>();
  const [hasNextPage, setHasNextPage] = useState(true);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const getProduct = async (data: {
    page: number;
    title: string;
    append: boolean;
  }) => {
    try {
      if (!storeID) return;
      setLoading(true);
      const res = await checkoutService.getAllStoreProducts(
        +storeID,
        baseService,
        {
          Limit: '100',
          Page: String(data?.page),
          Title: data?.title || '',
        },
      );
      if (res?.data?.Result) {
        if (res?.data?.TotalCount < 100) {
          setHasNextPage(false);
        }
        if (data?.append) {
          setProducts(prev => [...prev, ...(res?.data?.Result || [])]);
        } else {
          setProducts(res?.data?.Result || []);
        }
        setPage(prev => ({ ...prev, page: prev.page + 1 }));
      } else {
        setProducts([]);
        setPage({ page: 0, title: '' });
      }
      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const lastElementRef = useCallback(
    (node: HTMLLIElement) => {
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasNextPage) {
          getProduct({ ...page, append: true });
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [page],
  );

  const handleChangeDropdownWithDebounce = useCallback(
    debounce(val => {
      setPage({ page: 0, title: val });
      getProduct({ page: 0, title: val, append: false });
    }, 500),
    [],
  );

  const setRef = (value: number) => {
    position.current = value;
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Loader loading={loading} />

        <FieldLabel
          label={
            (
              <CustomTooltip title="Products">
                <span>Select product for upsell</span>
              </CustomTooltip>
            ) as React.ReactNode as string
          }
          el="div"
        />
        <AutoCompleteSelect
          onOpen={() => getProduct({ page: 0, title: '', append: false })}
          lastElementRef={lastElementRef}
          position={position.current}
          setPositionRef={value => setRef(value)}
          loading={loading}
          options={
            !loading
              ? products?.map(val => {
                  return {
                    value: val?.id,
                    label: `${val?.title} ($${val?.variants?.[0]?.price || 0}) (${val?.id})`,
                  };
                })
              : []
          }
          labelKey="label"
          onChange={e => {
            if (e == null) {
              onChange({
                product: null,
                variant: null,
              });
              return;
            }
            const product = products?.find(
              value => Number(value?.id) === e.value,
            );

            if (product)
              onChange({
                product: product,
                variant: product?.variants?.[0],
              });
          }}
          onInputChange={val => {
            handleChangeDropdownWithDebounce(val);
          }}
          placeholder="Select products"
          value={
            value?.product
              ? {
                  label: value?.product?.title,
                  value: value?.product?.id,
                }
              : null
          }
          className="w-full dropdown-field"
          label="Products"
        />
      </div>
      {value?.product?.variants?.length && (
        <div className="relative">
          <FieldLabel
            label={
              (
                <CustomTooltip title="Variants">
                  <span>Select Variant</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }
            el="div">
            <Select
              value={value?.variant?.id}
              placeholder="Select Variant"
              onChange={itemId => {
                const variant = value?.product?.variants?.find(
                  item => item?.id === itemId,
                );
                if (variant) {
                  onChange({ ...value, variant: variant });
                }
              }}
              options={value?.product?.variants?.map(variant => ({
                label:
                  variant?.title !== ''
                    ? variant?.title
                    : value?.product?.title,
                value: variant?.id,
              }))}
            />
          </FieldLabel>
        </div>
      )}
    </div>
  );
};

export default ShipmentProducts;
